import { forwardRef, useId, useState, type TextareaHTMLAttributes } from 'react';

import { cn } from '../../utils';

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
  hint?: string;
}

/**
 * Área de texto multilínea. Comparte el tratamiento visual del `Input`
 * —etiqueta flotante sobre el borde y superficie tokenizada— para que un
 * formulario que mezcla los dos no se vea de dos sistemas distintos.
 *
 * El fondo se declara con `bg-tkf-surface` a propósito: sin él, el `textarea`
 * hereda el blanco por defecto del navegador y en tema oscuro queda como un
 * recuadro blanco entre campos oscuros.
 */
export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ label, error, hint, className, id, disabled, rows = 4, onFocus, onBlur, ...props }, ref) => {
    const reactId = useId();
    const textareaId = id || `textarea-${reactId}`;
    const hasError = Boolean(error);
    const [isFocused, setIsFocused] = useState(false);

    const handleFocus = (e: React.FocusEvent<HTMLTextAreaElement>) => {
      setIsFocused(true);
      onFocus?.(e);
    };

    const handleBlur = (e: React.FocusEvent<HTMLTextAreaElement>) => {
      setIsFocused(false);
      onBlur?.(e);
    };

    return (
      <div className="w-full">
        <div className="relative">
          {label && (
            <label
              htmlFor={textareaId}
              style={{
                // Mitad superior transparente (deja ver el fondo de la página) y
                // mitad inferior del color del propio campo, para que la muesca
                // no deje asomar el borde. Mismo truco que el `Input`.
                background: disabled
                  ? 'linear-gradient(to bottom, transparent 45%, var(--tkf-surface-muted) 45%)'
                  : 'linear-gradient(to bottom, transparent 45%, var(--tkf-surface) 45%)',
              }}
              className={cn(
                'pointer-events-none absolute left-3 top-0 -translate-y-1/2',
                'px-1 text-sm font-medium transition-colors duration-200',
                hasError
                  ? 'text-tkf-fg-error'
                  : isFocused
                    ? 'text-primary-500'
                    : 'text-tkf-fg-muted',
                disabled && 'text-tkf-fg-disabled'
              )}
            >
              {label}
            </label>
          )}

          <textarea
            ref={ref}
            id={textareaId}
            rows={rows}
            onFocus={handleFocus}
            onBlur={handleBlur}
            className={cn(
              'block w-full rounded-lg border bg-tkf-surface px-3 py-2 text-sm text-tkf-fg transition-colors duration-200',
              'focus:outline-none focus:ring-1',
              'disabled:cursor-not-allowed disabled:bg-tkf-surface-muted disabled:text-tkf-fg-disabled',
              'placeholder:text-tkf-fg-subtle',
              'resize-y',
              hasError
                ? 'border-tkf-line-error focus:border-tkf-line-error focus:ring-tkf-line-error'
                : 'border-tkf-line-medium focus:border-primary-500 focus:ring-primary-500',
              className
            )}
            disabled={disabled}
            aria-invalid={hasError}
            aria-describedby={
              hasError ? `${textareaId}-error` : hint ? `${textareaId}-hint` : undefined
            }
            {...props}
          />
        </div>
        {error && (
          <p id={`${textareaId}-error`} className="mt-1 text-sm text-tkf-fg-error">
            {error}
          </p>
        )}
        {hint && !error && (
          <p id={`${textareaId}-hint`} className="mt-1 text-sm text-tkf-fg-muted">
            {hint}
          </p>
        )}
      </div>
    );
  }
);

Textarea.displayName = 'Textarea';
